import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  SELLER_ME_QUERY_KEY,
  sellerOnboardingApi,
  type SellerApplication,
} from '@modules/seller-onboarding/api';
import { isSellerOwner } from '@modules/seller-staff';
import { Modal, Button } from '@shared/ui';
import { openSellerDocument } from '../open-seller-document';
import './SellerOfferAcceptanceModal.css';

type SellerOfferAcceptanceModalProps = {
  sellerApp: SellerApplication | undefined;
};

export function SellerOfferAcceptanceModal({ sellerApp }: SellerOfferAcceptanceModalProps) {
  const queryClient = useQueryClient();

  const needsAcceptance = Boolean(
    sellerApp &&
    isSellerOwner(sellerApp) &&
    sellerApp.status === 'APPROVED' &&
    !sellerApp.seller?.accountBlock &&
    sellerApp.offerPdfFileUrl?.trim() &&
    !sellerApp.offerAccepted,
  );

  const acceptMutation = useMutation({
    mutationFn: () => sellerOnboardingApi.acceptOffer(),
    onSuccess: (next) => {
      queryClient.setQueryData(SELLER_ME_QUERY_KEY, next);
      void queryClient.invalidateQueries({ queryKey: ['seller-offer-documents'] });
      notifications.show({
        title: 'Принято',
        message: 'Вы согласились с условиями договора-оферты',
        color: 'green',
      });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message ?? 'Не удалось принять оферту';
      notifications.show({
        title: 'Ошибка',
        message: Array.isArray(message) ? message.join(', ') : String(message),
        color: 'red',
      });
    },
  });

  const pdfUrl = sellerApp?.offerPdfFileUrl?.trim() ?? '';

  return (
    <Modal
      size={440}
      isOpen={needsAcceptance}
      onClose={() => {}}
      title="Новая редакция договора-оферты"
      closeOnClickOutside={false}
      closeOnEscape={false}
      withCloseButton={false}
    >
      <div className="seller-offer-acceptance-modal">
        <p className="seller-offer-acceptance-modal__hint">
          Опубликована новая версия договора-оферты
          {sellerApp?.offerTemplateVersion != null
            ? ` (v${sellerApp.offerTemplateVersion})`
            : ''}
          . Ознакомьтесь с документом и подтвердите согласие, чтобы продолжить работу в
          кабинете.
        </p>

        {sellerApp?.offerTemplateUpdatedAt ? (
          <p className="seller-offer-acceptance-modal__meta">
            Дата обновления:{' '}
            {new Date(sellerApp.offerTemplateUpdatedAt).toLocaleDateString('ru-RU')}
          </p>
        ) : null}

        <div className="seller-offer-acceptance-modal__actions">
          {pdfUrl ? (
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => openSellerDocument(pdfUrl)}
            >
              Открыть PDF договора-оферты
            </Button>
          ) : null}

          <Button
            type="button"
            variant="primary"
            fullWidth
            loading={acceptMutation.isPending}
            disabled={acceptMutation.isPending}
            onClick={() => acceptMutation.mutate()}
          >
            Согласен с условиями
          </Button>
        </div>
      </div>
    </Modal>
  );
}
